import React, { useEffect, useRef } from 'react';

interface PrintableReportProps {
  opportunity: any;
  analysis: any;
  solicitationAnalysis: any;
  matches: any[];
  onClose: () => void;
}

/**
 * Renders a print-optimized report for an opportunity including:
 * - Opportunity details
 * - AI Analysis (requirements, risks, certifications)
 * - Solicitation Analysis (key details, contract structure, core requirements)
 * - Vendor Matches (FIT scores, strengths, gaps)
 *
 * Uses window.print() for browser-native Print / Save as PDF.
 */
export default function PrintableReport({ opportunity, analysis, solicitationAnalysis, matches, onClose }: PrintableReportProps) {
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-trigger print after a short delay for rendering
    const timeout = setTimeout(() => {
      window.print();
    }, 600);
    return () => clearTimeout(timeout);
  }, []);

  const opp = opportunity;
  const analysisOutput = analysis?.output;
  const solAnalysis = solicitationAnalysis;

  return (
    <div>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-report, #printable-report * { visibility: visible; }
          #printable-report { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          @page { margin: 0.75in; size: letter; }
        }
        @media screen {
          #printable-report { max-width: 900px; margin: 0 auto; padding: 32px; }
        }
        #printable-report {
          font-family: 'Segoe UI', -apple-system, sans-serif;
          color: #1a2744;
          font-size: 12px;
          line-height: 1.5;
        }
        #printable-report h1 { font-size: 20px; margin: 0 0 4px; color: #1a2744; }
        #printable-report h2 { font-size: 15px; margin: 20px 0 10px; color: #1a2744; border-bottom: 2px solid #2563eb; padding-bottom: 4px; page-break-after: avoid; }
        #printable-report h3 { font-size: 13px; margin: 14px 0 8px; color: #374151; page-break-after: avoid; }
        #printable-report .report-header { border-bottom: 3px solid #1a2744; padding-bottom: 12px; margin-bottom: 20px; }
        #printable-report .report-header .subtitle { font-size: 12px; color: #6b7280; }
        #printable-report .field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; margin-bottom: 12px; }
        #printable-report .field-label { font-size: 10px; color: #6b7280; text-transform: uppercase; font-weight: 600; letter-spacing: 0.3px; }
        #printable-report .field-value { font-size: 12px; color: #1f2937; margin-top: 1px; }
        #printable-report table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 11px; }
        #printable-report th { background: #f3f4f6; text-align: left; padding: 6px 8px; font-weight: 600; border-bottom: 1px solid #d1d5db; }
        #printable-report td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
        #printable-report .badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 10px; font-weight: 600; }
        #printable-report .badge-red { background: #fee2e2; color: #991b1b; }
        #printable-report .badge-green { background: #d1fae5; color: #065f46; }
        #printable-report .badge-blue { background: #dbeafe; color: #1e40af; }
        #printable-report .badge-yellow { background: #fef3c7; color: #92400e; }
        #printable-report .badge-purple { background: #ede9fe; color: #5b21b6; }
        #printable-report .section { margin-bottom: 16px; page-break-inside: avoid; }
        #printable-report .vendor-card { padding: 10px; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 10px; page-break-inside: avoid; }
        #printable-report .score-bar { height: 6px; background: #e5e7eb; border-radius: 3px; margin-top: 2px; }
        #printable-report .score-bar-fill { height: 100%; border-radius: 3px; }
        #printable-report .disclaimer { background: #fefce8; border-left: 3px solid #f59e0b; padding: 8px 12px; font-size: 11px; margin: 16px 0; }
        #printable-report ul { margin: 4px 0; padding-left: 16px; }
        #printable-report li { margin-bottom: 3px; }
        #printable-report .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #d1d5db; font-size: 10px; color: #9ca3af; text-align: center; }
      `}</style>

      {/* Toolbar (screen only) */}
      <div className="no-print" style={{ position: 'sticky', top: 0, background: '#1a2744', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 100 }}>
        <span style={{ color: 'white', fontSize: 14, fontWeight: 600 }}>FTI Procurement Report Preview</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => window.print()}
            style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 5, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            🖨️ Print / Save as PDF
          </button>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 5, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div id="printable-report" ref={reportRef}>
        {/* Header */}
        <div className="report-header">
          <h1>{opp?.title || 'Untitled Opportunity'}</h1>
          <div className="subtitle">
            FTI Procurement Intelligence Report &mdash; Generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="disclaimer">
          <strong>Notice:</strong> This report supports human procurement decision-making and does not replace
          the judgment or authority of a Contracting Officer. AI-generated analysis has been labeled accordingly.
        </div>

        {/* ═══ OPPORTUNITY DETAILS ═══ */}
        <h2>Opportunity Details</h2>
        <div className="section">
          <div className="field-grid">
            {opp?.notice_id && <div><div className="field-label">Notice ID</div><div className="field-value">{opp.notice_id}</div></div>}
            {opp?.agency && <div><div className="field-label">Agency</div><div className="field-value">{opp.agency}</div></div>}
            {opp?.office && <div><div className="field-label">Office</div><div className="field-value">{opp.office}</div></div>}
            {opp?.naics_code && <div><div className="field-label">NAICS Code</div><div className="field-value">{opp.naics_code}</div></div>}
            {opp?.psc_code && <div><div className="field-label">PSC Code</div><div className="field-value">{opp.psc_code}</div></div>}
            {opp?.set_aside && <div><div className="field-label">Set-Aside</div><div className="field-value">{opp.set_aside}</div></div>}
            {opp?.status && <div><div className="field-label">Status</div><div className="field-value">{opp.status}</div></div>}
            {opp?.posted_date && <div><div className="field-label">Posted Date</div><div className="field-value">{new Date(opp.posted_date).toLocaleDateString()}</div></div>}
            {opp?.response_deadline && <div><div className="field-label">Response Deadline</div><div className="field-value" style={{ color: '#dc2626', fontWeight: 600 }}>{new Date(opp.response_deadline).toLocaleString()}</div></div>}
            {opp?.place_of_performance && <div><div className="field-label">Place of Performance</div><div className="field-value">{opp.place_of_performance}</div></div>}
          </div>
          {opp?.description && (
            <div style={{ marginTop: 8 }}>
              <div className="field-label">Description</div>
              <div className="field-value" style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{opp.description}</div>
            </div>
          )}
        </div>

        {/* ═══ SOLICITATION ANALYSIS ═══ */}
        {solAnalysis && (
          <>
            <h2>Solicitation Analysis <span className="badge badge-purple">AI-Generated</span></h2>

            {solAnalysis.executive_summary && (
              <div className="section">
                <h3>Executive Summary</h3>
                <p>{solAnalysis.executive_summary}</p>
              </div>
            )}

            {solAnalysis.key_details && (
              <div className="section">
                <h3>Key Details</h3>
                <div className="field-grid">
                  {solAnalysis.key_details.solicitation_number && <div><div className="field-label">Solicitation #</div><div className="field-value">{solAnalysis.key_details.solicitation_number}</div></div>}
                  {solAnalysis.key_details.issuing_office && <div><div className="field-label">Issuing Office</div><div className="field-value">{solAnalysis.key_details.issuing_office}</div></div>}
                  {solAnalysis.key_details.offer_due_date && <div><div className="field-label">Offer Due</div><div className="field-value" style={{ color: '#dc2626' }}>{solAnalysis.key_details.offer_due_date}</div></div>}
                  {solAnalysis.key_details.naics_code && <div><div className="field-label">NAICS</div><div className="field-value">{solAnalysis.key_details.naics_code}</div></div>}
                  {solAnalysis.key_details.set_aside && <div><div className="field-label">Set-Aside</div><div className="field-value">{solAnalysis.key_details.set_aside}</div></div>}
                  {solAnalysis.key_details.acquisition_type && <div><div className="field-label">Acquisition Type</div><div className="field-value">{solAnalysis.key_details.acquisition_type}</div></div>}
                </div>
              </div>
            )}

            {solAnalysis.contract_structure && (
              <div className="section">
                <h3>Contract Structure</h3>
                <div className="field-grid">
                  {solAnalysis.contract_structure.contract_type && <div><div className="field-label">Contract Type</div><div className="field-value">{solAnalysis.contract_structure.contract_type}</div></div>}
                  {solAnalysis.contract_structure.base_period && <div><div className="field-label">Base Period</div><div className="field-value">{solAnalysis.contract_structure.base_period}</div></div>}
                  {solAnalysis.contract_structure.total_period_of_performance && <div><div className="field-label">Total Period</div><div className="field-value">{solAnalysis.contract_structure.total_period_of_performance}</div></div>}
                  {solAnalysis.contract_structure.evaluation_method && <div><div className="field-label">Evaluation</div><div className="field-value">{solAnalysis.contract_structure.evaluation_method}</div></div>}
                </div>
              </div>
            )}

            {solAnalysis.core_requirements?.length > 0 && (
              <div className="section">
                <h3>Core Requirements ({solAnalysis.core_requirements.length})</h3>
                <table>
                  <thead>
                    <tr><th>Requirement</th><th>Category</th><th>Mandatory</th></tr>
                  </thead>
                  <tbody>
                    {solAnalysis.core_requirements.map((req: any, idx: number) => (
                      <tr key={idx}>
                        <td>{req.requirement}</td>
                        <td><span className="badge badge-blue">{req.category}</span></td>
                        <td>{req.mandatory ? <span className="badge badge-red">Required</span> : <span className="badge badge-blue">Optional</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {solAnalysis.procurement_items?.length > 0 && (
              <div className="section">
                <h3>Procurement Items</h3>
                <table>
                  <thead>
                    <tr><th>Description</th><th>Qty</th><th>Products</th><th>Brand Name</th></tr>
                  </thead>
                  <tbody>
                    {solAnalysis.procurement_items.map((item: any, idx: number) => (
                      <tr key={idx}>
                        <td>{item.description}</td>
                        <td>{item.quantity || '—'}</td>
                        <td>{item.specific_products || '—'}</td>
                        <td>{item.brand_name_or_equal ? <span className="badge badge-yellow">Yes</span> : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {solAnalysis.submission_requirements && (
              <div className="section">
                <h3>Submission Requirements</h3>
                <div className="field-grid">
                  {solAnalysis.submission_requirements.submission_method && <div><div className="field-label">Method</div><div className="field-value">{solAnalysis.submission_requirements.submission_method}</div></div>}
                  {solAnalysis.submission_requirements.deadline && <div><div className="field-label">Deadline</div><div className="field-value" style={{ color: '#dc2626' }}>{solAnalysis.submission_requirements.deadline}</div></div>}
                  {solAnalysis.submission_requirements.page_limits && <div><div className="field-label">Page Limits</div><div className="field-value">{solAnalysis.submission_requirements.page_limits}</div></div>}
                  {solAnalysis.submission_requirements.format_requirements && <div><div className="field-label">Format</div><div className="field-value">{solAnalysis.submission_requirements.format_requirements}</div></div>}
                </div>
                {solAnalysis.submission_requirements.required_documents?.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div className="field-label">Required Documents</div>
                    <ul>
                      {solAnalysis.submission_requirements.required_documents.map((doc: string, idx: number) => (
                        <li key={idx}>{doc}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {solAnalysis.flags && (solAnalysis.flags.brand_name_or_equal_items?.length > 0 || solAnalysis.flags.potential_risks?.length > 0) && (
              <div className="section">
                <h3>Flags & Risks</h3>
                {solAnalysis.flags.brand_name_or_equal_items?.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div className="field-label">Brand Name or Equal Items</div>
                    <ul>{solAnalysis.flags.brand_name_or_equal_items.map((f: string, i: number) => <li key={i}>{f}</li>)}</ul>
                  </div>
                )}
                {solAnalysis.flags.unusual_restrictions?.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div className="field-label">Unusual Restrictions</div>
                    <ul>{solAnalysis.flags.unusual_restrictions.map((f: string, i: number) => <li key={i}>{f}</li>)}</ul>
                  </div>
                )}
                {solAnalysis.flags.potential_risks?.length > 0 && (
                  <div>
                    <div className="field-label">Potential Risks</div>
                    <ul>{solAnalysis.flags.potential_risks.map((f: string, i: number) => <li key={i}>{f}</li>)}</ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ═══ AI ANALYSIS ═══ */}
        {analysisOutput && (
          <>
            <h2>AI Requirements Analysis <span className="badge badge-purple">AI-Generated</span></h2>

            <div className="section">
              <div className="field-grid">
                {analysisOutput.objective && <div><div className="field-label">Objective</div><div className="field-value">{analysisOutput.objective}</div></div>}
                {analysisOutput.scope && <div><div className="field-label">Scope</div><div className="field-value">{analysisOutput.scope}</div></div>}
              </div>
            </div>

            {analysisOutput.requirements?.length > 0 && (
              <div className="section">
                <h3>Extracted Requirements ({analysisOutput.requirements.length})</h3>
                <table>
                  <thead>
                    <tr><th>#</th><th>Requirement</th><th>Category</th><th>Mandatory</th><th>Confidence</th></tr>
                  </thead>
                  <tbody>
                    {analysisOutput.requirements.slice(0, 30).map((req: any, idx: number) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{req.text}</td>
                        <td><span className="badge badge-blue">{req.category}</span></td>
                        <td>{req.mandatory ? <span className="badge badge-red">Yes</span> : 'No'}</td>
                        <td>{req.confidence ? `${Math.round(req.confidence * 100)}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {analysisOutput.certifications?.length > 0 && (
              <div className="section">
                <h3>Certifications Required</h3>
                <ul>{analysisOutput.certifications.map((c: string, i: number) => <li key={i}>{c}</li>)}</ul>
              </div>
            )}

            {analysisOutput.contract_vehicles?.length > 0 && (
              <div className="section">
                <h3>Contract Vehicles</h3>
                <ul>{analysisOutput.contract_vehicles.map((v: string, i: number) => <li key={i}>{v}</li>)}</ul>
              </div>
            )}

            {analysisOutput.risks?.length > 0 && (
              <div className="section">
                <h3>Identified Risks</h3>
                <ul>{analysisOutput.risks.map((r: string, i: number) => <li key={i}>{r}</li>)}</ul>
              </div>
            )}
          </>
        )}

        {/* ═══ VENDOR MATCHES ═══ */}
        {matches.length > 0 && (
          <>
            <h2>Vendor Match Results ({matches.length} vendors)</h2>

            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Vendor</th>
                  <th>FIT Score</th>
                  <th>Classification</th>
                  <th>Role</th>
                  <th>MC</th>
                  <th>DA</th>
                  <th>DR</th>
                  <th>SC</th>
                  <th>CF</th>
                </tr>
              </thead>
              <tbody>
                {matches.slice(0, 25).map((m: any, idx: number) => (
                  <tr key={m.vendor_id || idx}>
                    <td>{idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{m.vendor_name}</td>
                    <td style={{ fontWeight: 700 }}>{m.fit_score || 0}</td>
                    <td><span className={`badge ${(m.fit_score || 0) >= 90 ? 'badge-green' : (m.fit_score || 0) >= 80 ? 'badge-blue' : 'badge-yellow'}`}>{m.fit_classification || '—'}</span></td>
                    <td>{(m.fit_explanation?.recommended_role || m.recommended_role || '').replace(/_/g, ' ')}</td>
                    <td>{m.mission_capability_score || m.mission_capability || 0}</td>
                    <td>{m.domain_alignment_score || m.domain_alignment || 0}</td>
                    <td>{m.deployment_reality_score || m.deployment_reality || 0}</td>
                    <td>{m.solution_completeness_score || m.solution_completeness || 0}</td>
                    <td>{m.contract_fit_score || m.contract_fit || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Detailed Vendor Cards */}
            <h3>Vendor Details</h3>
            {matches.slice(0, 15).map((m: any, idx: number) => {
              const explanation = m.fit_explanation || m;
              const strengths = explanation.strengths || m.why_strong || [];
              const gaps = explanation.capability_gaps || m.gaps || [];
              const differentiator = explanation.key_differentiator || m.key_differentiator || '';
              const teaming = explanation.teaming_recommendation || m.teaming_recommendation || '';

              return (
                <div key={m.vendor_id || idx} className="vendor-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <strong>#{idx + 1} {m.vendor_name}</strong>
                    <span className={`badge ${(m.fit_score || 0) >= 90 ? 'badge-green' : (m.fit_score || 0) >= 80 ? 'badge-blue' : 'badge-yellow'}`}>
                      FIT: {m.fit_score || 0}/100
                    </span>
                  </div>
                  {differentiator && <div style={{ marginBottom: 4 }}><em>Key Differentiator:</em> {differentiator}</div>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {strengths.length > 0 && (
                      <div>
                        <div className="field-label" style={{ color: '#10b981' }}>Strengths</div>
                        <ul>{strengths.slice(0, 4).map((s: string, i: number) => <li key={i}>{s}</li>)}</ul>
                      </div>
                    )}
                    {gaps.length > 0 && (
                      <div>
                        <div className="field-label" style={{ color: '#dc2626' }}>Gaps</div>
                        <ul>{gaps.slice(0, 4).map((g: string, i: number) => <li key={i}>{g}</li>)}</ul>
                      </div>
                    )}
                  </div>
                  {teaming && <div style={{ marginTop: 4, fontSize: 11, color: '#4338ca' }}><strong>Teaming:</strong> {teaming}</div>}
                </div>
              );
            })}
          </>
        )}

        {/* Footer */}
        <div className="footer">
          <p>FTI Procurement Intelligence &mdash; Confidential &mdash; For authorized users only</p>
          <p>Generated: {new Date().toISOString()} | Opportunity ID: {opp?.id || 'N/A'}</p>
          <p>AI analyses generated using Amazon Bedrock. All AI outputs require human validation before procurement decisions.</p>
        </div>
      </div>
    </div>
  );
}
